import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const AccountsOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('payments');

  // States for different sections
  const [paymentVouchers, setPaymentVouchers] = useState([]);
  const [receiptVouchers, setReceiptVouchers] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);

  // Color palette
  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch accounts for dropdowns
      const accountsData = await apiGet('/admin/accounts/setup/accounts');
      if (accountsData.success) {
        setAccounts(accountsData.data);
      }

      if (activeTab === 'payments') {
        const data = await apiGet('/admin/accounts/operations/payment-vouchers');
        if (data.success) {
          setPaymentVouchers(data.data);
        }
      } else if (activeTab === 'receipts') {
        const data = await apiGet('/admin/accounts/operations/receipt-vouchers');
        if (data.success) {
          setReceiptVouchers(data.data);
        }
      } else if (activeTab === 'journal') {
        const data = await apiGet('/admin/accounts/operations/journal-entries');
        if (data.success) {
          setJournalEntries(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleCreatePayment = async (formData) => {
    try {
      const response = await fetch('/admin/accounts/operations/payment-vouchers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        alert('Payment voucher created successfully!');
        setShowPaymentModal(false);
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('Error creating payment voucher');
    }
  };

  const handleCreateReceipt = async (formData) => {
    try {
      const response = await fetch('/admin/accounts/operations/receipt-vouchers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        alert('Receipt voucher created successfully!');
        setShowReceiptModal(false);
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating receipt:', error);
      alert('Error creating receipt voucher');
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    headerContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '12px' : '16px'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : '14px',
      color: colors.textLight
    },
    tabContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      flexWrap: 'wrap'
    },
    tab: (active) => ({
      padding: isMobile ? '10px 16px' : '12px 24px',
      background: active ? colors.primary : colors.white,
      color: active ? colors.white : colors.textDark,
      border: `2px solid ${active ? colors.primary : colors.mediumGray}`,
      borderRadius: '10px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }),
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '16px' : '20px'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background 0.2s'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    th: {
      background: colors.lightGray,
      padding: isMobile ? '10px' : '12px',
      textAlign: 'left',
      fontWeight: '700',
      borderBottom: `2px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '11px' : '13px'
    },
    td: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    badge: (status) => ({
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      background: status === 'paid' || status === 'received' ? `${colors.success}20` : `${colors.warning}20`,
      color: status === 'paid' || status === 'received' ? colors.success : colors.warning
    })
  };

  const PaymentVoucherModal = () => {
    const [formData, setFormData] = useState({
      voucher_date: new Date().toISOString().split('T')[0],
      payment_to: '',
      payment_type: 'expense',
      payment_method: 'cash',
      amount: '',
      account_id: '3', // Cash in hand
      expense_account_id: '',
      description: '',
      reference_number: '',
      created_by: 'Admin'
    });

    const expenseAccounts = accounts.filter(a => a.account_type === 'expense');
    const paymentAccounts = accounts.filter(a => ['1101', '1102'].includes(a.account_code)); // Cash and Bank

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: colors.white,
          padding: isMobile ? '20px' : '30px',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: isMobile ? '18px' : '22px' }}>New Payment Voucher</h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Date</label>
              <input
                type="date"
                value={formData.voucher_date}
                onChange={(e) => setFormData({ ...formData, voucher_date: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Pay To</label>
              <input
                type="text"
                value={formData.payment_to}
                onChange={(e) => setFormData({ ...formData, payment_to: e.target.value })}
                placeholder="Recipient name"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Payment Type</label>
                <select
                  value={formData.payment_type}
                  onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                >
                  <option value="supplier">Supplier</option>
                  <option value="employee">Employee</option>
                  <option value="utility">Utility</option>
                  <option value="expense">Expense</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Payment Method</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      payment_method: e.target.value,
                      account_id: e.target.value === 'cash' ? '3' : '4' // Cash or Bank
                    });
                  }}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Expense Account</label>
              <select
                value={formData.expense_account_id}
                onChange={(e) => setFormData({ ...formData, expense_account_id: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              >
                <option value="">Select account</option>
                {expenseAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Payment description"
                rows="3"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Reference Number</label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="Cheque no / Transaction ID (optional)"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowPaymentModal(false)}
              style={{
                padding: '10px 20px',
                background: colors.mediumGray,
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleCreatePayment(formData)}
              style={styles.button}
            >
              Create Payment
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ReceiptVoucherModal = () => {
    const [formData, setFormData] = useState({
      voucher_date: new Date().toISOString().split('T')[0],
      received_from: '',
      receipt_type: 'other',
      payment_method: 'cash',
      amount: '',
      account_id: '3', // Cash in hand
      revenue_account_id: '',
      description: '',
      reference_number: '',
      created_by: 'Admin'
    });

    const revenueAccounts = accounts.filter(a => a.account_type === 'revenue');

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: colors.white,
          padding: isMobile ? '20px' : '30px',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: isMobile ? '18px' : '22px' }}>New Receipt Voucher</h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Date</label>
              <input
                type="date"
                value={formData.voucher_date}
                onChange={(e) => setFormData({ ...formData, voucher_date: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Received From</label>
              <input
                type="text"
                value={formData.received_from}
                onChange={(e) => setFormData({ ...formData, received_from: e.target.value })}
                placeholder="Payer name"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Receipt Type</label>
                <select
                  value={formData.receipt_type}
                  onChange={(e) => setFormData({ ...formData, receipt_type: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                >
                  <option value="customer">Customer</option>
                  <option value="room_revenue">Room Revenue</option>
                  <option value="food_revenue">Food & Beverage</option>
                  <option value="banquet_revenue">Banquet Revenue</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Payment Method</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      payment_method: e.target.value,
                      account_id: e.target.value === 'cash' ? '3' : '4'
                    });
                  }}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Revenue Account</label>
              <select
                value={formData.revenue_account_id}
                onChange={(e) => setFormData({ ...formData, revenue_account_id: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              >
                <option value="">Select account</option>
                {revenueAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Receipt description"
                rows="3"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Reference Number</label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="Transaction ID (optional)"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowReceiptModal(false)}
              style={{
                padding: '10px 20px',
                background: colors.mediumGray,
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleCreateReceipt(formData)}
              style={styles.button}
            >
              Create Receipt
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.headerContainer}>
          <div style={{ flex: 1 }}>
            <h1 style={styles.pageTitle}>Accounts Operations</h1>
            <p style={styles.pageSubtitle}>
              {isMobile ? 'Transactions' : 'Manage financial transactions, payments, and receipts'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={styles.tab(activeTab === 'payments')}
          onClick={() => setActiveTab('payments')}
        >
          💳 Payments
        </button>
        <button
          style={styles.tab(activeTab === 'receipts')}
          onClick={() => setActiveTab('receipts')}
        >
          💰 Receipts
        </button>
        <button
          style={styles.tab(activeTab === 'journal')}
          onClick={() => setActiveTab('journal')}
        >
          📔 Journal Entries
        </button>
      </div>

      {/* Content */}
      <div style={styles.contentCard}>
        <div style={styles.headerRow}>
          <h2 style={styles.sectionTitle}>
            {activeTab === 'payments' && '💳 Payment Vouchers'}
            {activeTab === 'receipts' && '💰 Receipt Vouchers'}
            {activeTab === 'journal' && '📔 Journal Entries'}
          </h2>
          {activeTab === 'payments' && (
            <button style={styles.button} onClick={() => setShowPaymentModal(true)}>
              + New Payment
            </button>
          )}
          {activeTab === 'receipts' && (
            <button style={styles.button} onClick={() => setShowReceiptModal(true)}>
              + New Receipt
            </button>
          )}
        </div>

        {/* Payment Vouchers Table */}
        {activeTab === 'payments' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Voucher #</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Pay To</th>
                  {!isMobile && <th style={styles.th}>Type</th>}
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentVouchers.map(voucher => (
                  <tr key={voucher.id}>
                    <td style={styles.td}><strong>{voucher.voucher_number}</strong></td>
                    <td style={styles.td}>{new Date(voucher.voucher_date).toLocaleDateString('en-IN')}</td>
                    <td style={styles.td}>{voucher.payment_to}</td>
                    {!isMobile && <td style={styles.td}>{voucher.payment_type}</td>}
                    <td style={styles.td}>
                      <strong>₹{parseFloat(voucher.amount).toLocaleString('en-IN')}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(voucher.status)}>
                        {voucher.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Receipt Vouchers Table */}
        {activeTab === 'receipts' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Voucher #</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Received From</th>
                  {!isMobile && <th style={styles.th}>Type</th>}
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {receiptVouchers.map(voucher => (
                  <tr key={voucher.id}>
                    <td style={styles.td}><strong>{voucher.voucher_number}</strong></td>
                    <td style={styles.td}>{new Date(voucher.voucher_date).toLocaleDateString('en-IN')}</td>
                    <td style={styles.td}>{voucher.received_from}</td>
                    {!isMobile && <td style={styles.td}>{voucher.receipt_type}</td>}
                    <td style={styles.td}>
                      <strong>₹{parseFloat(voucher.amount).toLocaleString('en-IN')}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(voucher.status)}>
                        {voucher.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Journal Entries Table */}
        {activeTab === 'journal' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Entry #</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Description</th>
                  {!isMobile && <th style={styles.th}>Type</th>}
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {journalEntries.map(entry => (
                  <tr key={entry.id}>
                    <td style={styles.td}><strong>{entry.entry_number}</strong></td>
                    <td style={styles.td}>{new Date(entry.entry_date).toLocaleDateString('en-IN')}</td>
                    <td style={styles.td}>{entry.description}</td>
                    {!isMobile && <td style={styles.td}>{entry.reference_type}</td>}
                    <td style={styles.td}>
                      <strong>₹{parseFloat(entry.total_debit).toLocaleString('en-IN')}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(entry.status)}>
                        {entry.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPaymentModal && <PaymentVoucherModal />}
      {showReceiptModal && <ReceiptVoucherModal />}
    </AdminLayout>
  );
};

export default AccountsOperations;